import { defineStore } from 'pinia'
import type {
  AddDonationParams,
  CancelDonationParams,
  GetDonationBoxResult,
  RecurringDonationId,
  WebsiteRecurringDonation
} from '~/types/donations'
import { noResponseFetch } from '~/utils/webSockets'
import statusMessages from '/@lang/status-messages.json'
import type { ApiResponse } from '#shared/types/utils'
import { usePersist } from '~/plugins/pinia.ts'

interface State {
  donationBoxes: GetDonationBoxResult[]
  hasLoadedDonationBoxes: boolean
  donationWebsocketUUID: string | undefined
}

const recurringDonationStatusMessages =
  statusMessages['recurring-donation-status-messages']

const donationBoxStatusMessages = statusMessages['collection-boxes-status-messages']

export const useDonationsStore = defineStore('donations', {
  state: (): State => ({
    donationBoxes: [],
    hasLoadedDonationBoxes: false,
    donationWebsocketUUID: undefined
  }),
  persist: usePersist<State>({
    storage: piniaPluginPersistedstate.cookies(),
    pick: ['donationWebsocketUUID']
  }),
  logout: {
    pick: ['donationWebsocketUUID']
  },
  actions: {
    getDonationUUID() {
      if (!this.donationWebsocketUUID) {
        this.donationWebsocketUUID = crypto.randomUUID()
      }

      return this.donationWebsocketUUID
    },

    clearDonationUUID() {
      this.donationWebsocketUUID = undefined
    },

    async createDonation(
      params: AddDonationParams & { identifier: string }
    ): Promise<string | false> {
      try {
        const result = await this.api.put<{ redirectUrl: string }>(
          'donation/create-donation',
          params
        )

        return result.redirectUrl
      } catch {
        return false
      }
    },

    async getRecurringDonations(): Promise<WebsiteRecurringDonation[] | false> {
      try {
        const { data } =
          await this.api.get<ApiResponse<WebsiteRecurringDonation[]>>(
            'recurring-donations'
          )
        return data
      } catch {
        addToast().error(
          recurringDonationStatusMessages[this.currentLocale].error['get-recurring']
        )
        return false
      }
    },

    async cancelRecurringDonation(id: RecurringDonationId): Promise<boolean> {
      return noResponseFetch.put({
        url: `recurring-donations/${id}/cancel`,
        success:
          recurringDonationStatusMessages[useLocaleStore().locale].success['cancel'],
        error: recurringDonationStatusMessages[useLocaleStore().locale].error['cancel']
      })
    },

    async getDonationBoxes(): Promise<GetDonationBoxResult[] | false> {
      try {
        const { data: donationBoxes } = await $fetch<ApiResponse<GetDonationBoxResult[]>>(
          '/api/donation-box/fetch-for-user'
        )
        this.donationBoxes = donationBoxes
        this.hasLoadedDonationBoxes = true
        return donationBoxes
      } catch {
        addToast().error(
          donationBoxStatusMessages[useLocaleStore().locale].error['fetch']
        )
        return false
      }
    },

    async requestCancellation(params: CancelDonationParams): Promise<boolean> {
      return noResponseFetch.post({
        url: 'recurring-donations/request-cancellation',
        payload: params
      })
    }
  }
})

/**
 * Pinia supports Hot Module replacement, so you can edit your stores and
 * interact with them directly in your app without reloading the page.
 *
 * @see https://pinia.esm.dev/cookbook/hot-module-replacement.html
 * @see https://vitejs.dev/guide/api-hmr.html
 */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDonationsStore as any, import.meta.hot))
}
